/* Native scrolling on mobile; existing section controllers own desktop. */
if (!customElements.get('clearance-mobile-carousel')) {
  customElements.define('clearance-mobile-carousel', class extends HTMLElement {
    connectedCallback() {
      this.root = document.getElementById(this.dataset.root);
      if (!this.root) return;
      this.media = matchMedia('(max-width: 749px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.media.addEventListener('change', () => this.configure(), options);
      this.motion.addEventListener('change', () => this.states?.forEach(s => this.schedule(s)), options);
      document.addEventListener('visibilitychange', () => this.states?.forEach(s => this.schedule(s)), options);
      document.addEventListener('shopify:block:select', event => this.selectBlock(event), options);
      document.addEventListener('shopify:block:deselect', () => {
        this.states?.forEach(s => { s.editing = false; this.schedule(s); });
      }, options);
      this.configure();
    }

    disconnectedCallback() {
      this.teardown();
      this.lifecycle?.abort();
    }

    configure() {
      this.teardown();
      if (this.media.matches) this.root.classList.add('cm-active');
      this.listeners = new AbortController();
      this.states = [...this.root.querySelectorAll(this.dataset.tracks)].map((track, index) => this.setup(track, index));
      this.resize = new ResizeObserver(() => this.states.forEach(s => this.refresh(s)));
      this.intersection = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const state = this.states.find(s => s.track === entry.target);
          if (state) { state.visible = entry.isIntersecting; this.schedule(state); }
        });
      });
      this.states.forEach(s => {
        this.resize.observe(s.track);
        s.items.forEach(item => this.resize.observe(item));
        this.intersection.observe(s.track);
        this.refresh(s);
      });
    }

    listen(element, type, handler, options = {}) {
      element.addEventListener(type, handler, { ...options, signal: this.listeners.signal });
    }

    setup(track, index) {
      const items = [...track.children];
      const saved = new Map([track, ...items].map(node => [node, {
        id: node.getAttribute('id'), role: node.getAttribute('role'),
        label: node.getAttribute('aria-label'), description: node.getAttribute('aria-roledescription'),
        tabindex: node.getAttribute('tabindex'), hidden: node.getAttribute('aria-hidden'), inert: node.inert,
      }]));
      track.classList.add('cm-track');
      track.dataset.cmSwipe = this.dataset.swipe;
      track.id ||= `${this.dataset.root}-mobile-track-${index}`;
      track.setAttribute('role', 'region');
      track.setAttribute('aria-roledescription', 'carousel');
      track.setAttribute('aria-label', track.getAttribute('aria-label') || this.root.querySelector('h2')?.textContent || 'Promotions');
      track.tabIndex = 0;
      items.forEach(item => item.classList.add('cm-item'));
      const controls = document.createElement('div');
      controls.className = 'cm-controls';
      controls.innerHTML = '<button class="cm-button cm-prev" type="button" aria-label="Previous items">←</button><div class="cm-dots"></div><button class="cm-button cm-next" type="button" aria-label="Next items">→</button><button class="cm-button cm-pause" type="button">Pause</button><span class="visually-hidden" aria-live="polite" aria-atomic="true"></span>';
      track.after(controls);
      const state = { track, items, saved, controls, points: [], index: 0, visible: false, paused: false };
      state.prev = controls.querySelector('.cm-prev');
      state.next = controls.querySelector('.cm-next');
      state.dots = controls.querySelector('.cm-dots');
      state.pause = controls.querySelector('.cm-pause');
      state.status = controls.querySelector('[aria-live]');
      // With swipe disabled and no requested controls, retain arrows for access.
      const arrows = this.dataset.arrows === 'true' || (this.dataset.swipe === 'false' && this.dataset.dots !== 'true');
      state.prev.hidden = state.next.hidden = !arrows;
      state.dots.hidden = this.dataset.dots !== 'true';
      state.pause.hidden = this.dataset.autoplay !== 'true';
      controls.querySelectorAll('button').forEach(button => button.setAttribute('aria-controls', track.id));
      this.listen(state.prev, 'click', () => this.go(state, state.index - 1));
      this.listen(state.next, 'click', () => this.go(state, state.index + 1));
      this.listen(state.pause, 'click', () => {
        state.paused = !state.paused;
        state.pause.textContent = state.paused ? 'Play' : 'Pause';
        this.schedule(state);
      });
      this.listen(track, 'scroll', () => {
        cancelAnimationFrame(state.frame);
        state.frame = requestAnimationFrame(() => { this.update(state); this.schedule(state); });
      }, { passive: true });
      this.listen(track, 'keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = state.index + ((event.key === 'ArrowRight') !== state.rtl ? 1 : -1);
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = state.points.length - 1;
        this.go(state, next);
      });
      [track, controls].forEach(element => {
        this.listen(element, 'focusin', () => { state.focus = true; this.schedule(state); });
        this.listen(element, 'focusout', event => {
          state.focus = track.contains(event.relatedTarget) || controls.contains(event.relatedTarget);
          this.schedule(state);
        });
        this.listen(element, 'pointerenter', event => { state.hover = event.pointerType === 'mouse'; this.schedule(state); });
        this.listen(element, 'pointerleave', () => { state.hover = false; this.schedule(state); });
        this.listen(element, 'pointerdown', () => { state.touch = true; this.schedule(state); });
      });
      ['pointerup', 'pointercancel'].forEach(type => this.listen(document, type, () => {
        state.touch = false; this.schedule(state);
      }));
      return state;
    }

    refresh(state) {
      if (!state.track.clientWidth) { this.schedule(state); return; }
      state.rtl = getComputedStyle(state.track).direction === 'rtl';
      const max = Math.max(0, state.track.scrollWidth - state.track.clientWidth);
      const first = state.items[0]?.getBoundingClientRect();
      const points = state.items.map(item => {
        const rect = item.getBoundingClientRect();
        return Math.min(max, Math.max(0, state.rtl ? first.right - rect.right : rect.left - first.left));
      }).filter((point, index, all) => index === 0 || Math.abs(point - all[index - 1]) > 1);
      if (JSON.stringify(points) !== JSON.stringify(state.points)) {
        state.points = points;
        state.dots.replaceChildren();
        points.forEach((point, index) => {
          const dot = document.createElement('button');
          dot.type = 'button'; dot.className = 'cm-button cm-dot';
          dot.setAttribute('aria-label', `Go to view ${index + 1} of ${points.length}`);
          dot.setAttribute('aria-controls', state.track.id);
          dot.addEventListener('click', () => this.go(state, index));
          state.dots.append(dot);
        });
      }
      state.controls.hidden = points.length < 2 || (state.prev.hidden && state.dots.hidden && state.pause.hidden);
      this.update(state);
      this.schedule(state);
    }

    update(state) {
      const position = Math.abs(state.track.scrollLeft);
      state.index = state.points.reduce((best, point, index) =>
        Math.abs(point - position) < Math.abs(state.points[best] - position) ? index : best, 0);
      state.prev.disabled = state.index === 0;
      state.next.disabled = state.index >= state.points.length - 1;
      [...state.dots.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === state.index)));
      const bounds = state.track.getBoundingClientRect();
      state.items.forEach(item => {
        const rect = item.getBoundingClientRect();
        const visible = rect.right > bounds.left + 2 && rect.left < bounds.right - 2;
        item.setAttribute('aria-hidden', String(!visible));
        item.inert = !visible;
      });
    }

    go(state, index, automatic = false) {
      if (!state.points.length) return;
      const target = Math.max(0, Math.min(index, state.points.length - 1));
      state.track.scrollTo({ left: (state.rtl ? -1 : 1) * (state.points[target] || 0), behavior: this.motion.matches ? 'instant' : 'smooth' });
      if (!automatic) state.status.textContent = `View ${target + 1} of ${state.points.length}`;
      this.schedule(state);
    }

    schedule(state) {
      clearTimeout(state.timer);
      if (this.dataset.autoplay !== 'true' || this.motion.matches || document.hidden || !state.visible ||
          !state.track.clientWidth || state.points.length < 2 || state.paused || state.focus || state.hover || state.touch || state.editing) return;
      state.timer = setTimeout(() => this.go(state, (state.index + 1) % state.points.length, true), Math.max(2000, Number(this.dataset.speed) || 5000));
    }

    selectBlock(event) {
      if (!this.media.matches || !this.root.contains(event.target)) return;
      const panel = event.target.closest('[data-panel]');
      if (panel) this.root.querySelector(`[data-tab="${panel.dataset.panel}"]`)?.click();
      requestAnimationFrame(() => this.states?.forEach(state => {
        const index = state.items.findIndex(item => item === event.target || item.contains(event.target));
        if (index < 0) return;
        state.editing = true;
        this.refresh(state);
        this.go(state, Math.min(index, state.points.length - 1));
      }));
    }

    teardown() {
      this.listeners?.abort();
      this.resize?.disconnect();
      this.intersection?.disconnect();
      this.states?.forEach(state => {
        clearTimeout(state.timer); cancelAnimationFrame(state.frame);
        state.controls.remove();
        state.track.classList.remove('cm-track');
        delete state.track.dataset.cmSwipe;
        state.saved.forEach((saved, node) => {
          node.classList.remove('cm-item');
          for (const [key, attr] of Object.entries({id: 'id', role: 'role', label: 'aria-label', description: 'aria-roledescription', tabindex: 'tabindex', hidden: 'aria-hidden'})) {
            if (saved[key] === null) node.removeAttribute(attr); else node.setAttribute(attr, saved[key]);
          }
          node.inert = saved.inert;
        });
      });
      this.states = [];
      this.root?.classList.remove('cm-active');
    }
  });
}
